<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import type { StepCategory } from 'types/course/step';
import ScoreChip from 'shared/components/ScoreChip/ScoreChip.vue';
import TimerChip from 'shared/components/TimerChip/TimerChip.vue';

const showErrorCount = ref(true);
const showCorrectCount = ref(true);

type Props = {
  isTheory?: boolean;
  errorCount?: number;
  correctCount?: number;
  score?: number;
  category?: StepCategory;
  title?: string;
  subtitle?: string;
  isCompletedStep?: boolean;
  formattedTime?: string;
  /** The time limit is about to run out (Speed Round countdown). */
  timeWarning?: boolean;
  isSkippedStep?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  isTheory: false,
  errorCount: 0,
  correctCount: 0,
  score: 0,
  category: 'Practice',
  title: '',
  subtitle: '',
  isCompletedStep: false,
  formattedTime: '00:00',
  timeWarning: false,
  isSkippedStep: false,
});

watch(
  () => props.errorCount,
  async () => {
    showErrorCount.value = false;
    await nextTick();
    showErrorCount.value = true;
  },
);
watch(
  () => props.correctCount,
  async () => {
    showCorrectCount.value = false;
    await nextTick();
    showCorrectCount.value = true;
  },
);
</script>

<template>
  <div class="step-header">
    <q-toolbar
      v-if="!isTheory"
      class="q-px-none row"
    >
      <div class="stats row items-center no-wrap full-width">
        <transition
          appear
          enter-active-class="animated flipInY slower"
          leave-active-class="animated bounceOutUp"
        >
          <div
            v-show="showErrorCount"
            class="q-mr-sm"
          >
            <q-badge
              :key="errorCount"
              rounded
              class="justify-center counter-size text-subtitle1 q-py-none"
              color="negative"
              :label="errorCount"
              align="middle"
            />
          </div>
        </transition>
        <transition
          appear
          enter-active-class="animated flipInY slower"
          leave-active-class="animated bounceOutUp"
        >
          <div v-show="showCorrectCount">
            <q-badge
              rounded
              class="justify-center counter-size text-subtitle1 q-py-none"
              color="positive"
              :label="correctCount"
              align="middle"
            />
          </div>
        </transition>

        <q-space />

        <div>
          <transition
            appear
            enter-active-class="animated bounceInDown slower"
            leave-active-class="animated fadeOut slow"
          >
            <div v-show="isCompletedStep">
              <ScoreChip
                class="gradient-border"
                size="sm"
              >
                <span class="text-h6">
                  {{ score }}
                </span>
              </ScoreChip>
            </div>
          </transition>
        </div>
        <div>
          <TimerChip
            v-if="!isCompletedStep && !isSkippedStep"
            class="gradient-border"
            :class="{ 'animated pulse infinite': timeWarning }"
            :color="timeWarning ? 'negative' : undefined"
            :text-color="timeWarning ? 'white' : undefined"
          >
            {{ formattedTime }}
          </TimerChip>
        </div>
      </div>
    </q-toolbar>
    <q-item
      v-else
      class="q-pa-none"
    >
      <q-item-section>
        <q-item-label class="text-h6">{{ title }}</q-item-label>
        <q-item-label
          v-if="subtitle"
          class="text-subtitle2"
          >{{ subtitle }}</q-item-label
        >
      </q-item-section>
    </q-item>
  </div>
</template>

<style scoped>
.counter-size {
  min-width: 2.75rem;
  max-height: 1.5em;
}
</style>
